import { fromJS, List, Map } from 'immutable';
import { dispatch, regEventDb, registerHandler, subscribe } from 'nike-re-framejs';

import ticketusClient from '@/_http/axiosTicketusClient';
import '@/betslip/subs';
import { defaultMiddlewares } from '@/common/util';
import { deleteInLS, removedOldTicketsFromLS, ticket2LS, ticketsFromLS } from './utils';
import { deleteSavedTicket, fetchProfileSavedTickets } from './api';
import { catcher } from '@/_http/utils';
import { SAVED_TICKETS } from '@/routing/routes';
import { navigate } from '@/routing';

const LIMIT = 20;

regEventDb('ticketus/device-saved/toLS', defaultMiddlewares, (db, [ticketId]) => {
    ticket2LS(ticketId);
    return db;
});

registerHandler('ticketus/device-saved/fetch', defaultMiddlewares, (db, [ticketIds]) => {
    ticketIds = removedOldTicketsFromLS(ticketIds);
    if (!ticketIds?.length) {
        return db.setIn(['ticketus', 'device-saved'], List());
    }

    ticketusClient
        .get('/saved-tickets/minimal', {
            params: { ticket_ids: ticketIds }
        })
        .then(tickets => dispatch(['ticketus/device-saved/fetched', tickets]))
        .catch(catcher.swallow);

    return db;
});

function cleanDeviceSavedTickets(ticketusSavedTickets) {
    const ticketsIdsLS = ticketsFromLS();
    const ticketusIds = ticketusSavedTickets.map(ticket => ticket.ticketId);
    const idsToDelete = ticketsIdsLS.filter(id => !ticketusIds.includes(id));
    idsToDelete.forEach(id => deleteInLS(id));
}

registerHandler('ticketus/device-saved/fetched', defaultMiddlewares, (db, [tickets]) => {
    cleanDeviceSavedTickets(tickets);
    return db.setIn(['ticketus', 'device-saved'], fromJS(tickets.reverse()));
});

export const deleteTicketSaved = ticket => db => {
    deleteSavedTicket(ticket.ticketId).then(() => dispatch(removeFromProfileSaved(ticket)));
    return db.removeIn(['ticketus', 'detail', ticket.hash]);
};

registerHandler('ticketus/profile-saved/initial-load', defaultMiddlewares, db => {
    const loggedIn = db.get('loggedIn');
    if (!loggedIn) {
        return db.setIn(['ticketus', 'profile-saved'], fromJS({ list: [] }));
    }
    fetchProfileSavedTickets(0, LIMIT);
    // we can return db, because subscription handles the case when ticketus.profile-saved is not defined
    // and it is useful to return previous results while new ones are being fetched
    return db;
});

registerHandler('ticketus/profile-saved/load-more', defaultMiddlewares, db => {
    const hasNextPage = db.getIn(['ticketus', 'profile-saved', 'hasNextPage'], true);
    const offset = db.getIn(['ticketus', 'profile-saved', 'list'], List()).size;
    if (hasNextPage) {
        fetchProfileSavedTickets(offset, LIMIT);
    }
    return db;
});

registerHandler('ticketus/profile-saved/loaded', defaultMiddlewares, (db, [response, offset]) =>
    db.updateIn(['ticketus', 'profile-saved'], (savedDb = Map()) => {
        const hasNextPage = db => db.set('hasNextPage', response.get('hasNextPage'));
        const concatResults = db =>
            db.update('list', (list = List()) =>
                list
                    .concat(response.get('data', List()))
                    .toOrderedSet()
                    .toList()
            );
        const replaceResults = db =>
            db.set(
                'list',
                response
                    .get('data', List())
                    .toOrderedSet()
                    .toList()
            );
        if (offset) {
            return hasNextPage(concatResults(savedDb));
        }
        return hasNextPage(replaceResults(savedDb));
    })
);

subscribe(['betslip/savedBetslipParams'])
    .map(savedBetslipParams => {
        if (savedBetslipParams) {
            dispatch(['ticketus/profile-saved/initial-load']);
        }
        return null;
    })
    .deref();

function postAliasToTicketus(request) {
    return ticketusClient.post(`/saved-tickets/user-aliases`, request);
}

registerHandler('ticketus/setTicketAlias', defaultMiddlewares, (db, [request]) => {
    Promise.resolve()
        .then(() => dispatch(['ticketus/cleanTicketAliasError']))
        .then(() => postAliasToTicketus(request))
        .then(() => dispatch(['ticketus/setIsAliasSaved']))
        .catch(error => dispatch(['ticketus/setTicketAliasError', error.response.data]));
    return db;
});

registerHandler('ticketus/setTicketAliasError', defaultMiddlewares, (db, [error]) =>
    db.setIn(['ticketus', 'profile-saved', 'aliasError'], error)
);

registerHandler('ticketus/cleanTicketAliasError', defaultMiddlewares, db =>
    db.deleteIn(['ticketus', 'profile-saved', 'aliasError']).deleteIn(['ticketus', 'profile-saved', 'aliasSaved'])
);

registerHandler('ticketus/setIsAliasSaved', defaultMiddlewares, db =>
    db.setIn(['ticketus', 'profile-saved', 'aliasSaved'], true)
);
const removeFromProfileSaved = ticket => db => {
    const index = db.getIn(['ticketus', 'profile-saved', 'list']).findIndex(t => t.get('ticketId') === ticket.ticketId);
    navigate(SAVED_TICKETS);
    return db.deleteIn(['ticketus', 'profile-saved', 'list', index]);
};
